import React, { useCallback } from "react";
import type { ComponentMessageType } from "../../../types/ComponentMessageType";
import styles from "../message.module.scss";
import { useTranslation } from "react-i18next";
import { stripNumberStringToOnlyNumbers } from "@whatslly/commons/utils";
import { messagesModals } from "../../../stores/messagesModals";
import { BaseModal, IModalInstance, ModalHeader } from "@whatslly/ui-components/modals";
import { SvgV2 } from "@whatslly/ui-components/v2/BaseElements/SvgV2";

interface IContactProps {
  contacts: Exclude<ComponentMessageType["contacts"], undefined>;
  startChat?: (data: { firstName: string; lastName: string; phone: string }) => void;
  testId?: string;
}

function getContactDetials(contact: IContactProps["contacts"][number], overridePhone?: string) {
  const firstName = contact.name.first_name ?? contact.name.formatted_name.split(" ").at(0) ?? "";
  const lastName = contact.name.last_name ?? contact.name.formatted_name.split(" ").at(-1) ?? "";
  const phone = overridePhone ?? stripNumberStringToOnlyNumbers(contact.phones?.find((p) => p.phone)?.phone ?? "");
  return { firstName, lastName, phone };
}

const PreviewContactModal: React.FC<IContactProps & IModalInstance> = ({ contacts, startChat, closeModal }) => {
  const { t } = useTranslation();

  const _startChat = useCallback(
    (contact: IContactProps["contacts"][number], phone: string) => {
      startChat?.(getContactDetials(contact, stripNumberStringToOnlyNumbers(phone)));
      closeModal();
    },
    [startChat, closeModal]
  );

  return (
    <BaseModal
      header={<ModalHeader closeModal={closeModal}>{t("chat_component.contact.modal_header", { count: contacts.length })}</ModalHeader>}
      closeOnClickOutside={true}
      closeOnEscape={true}
      closeModal={closeModal}
      className={styles.previewContacts}
      headerClassName={styles.previewContactsHeader}
      bodyClassName={styles.previewContactsBody}
      testId={`preview-contact-modal`}
    >
      {contacts.map((contact, index) => (
        <div key={index} className={styles.contactItem}>
          <div className={styles.contactHeader}>
            <div className={styles.contactInitial}>{contact.name.formatted_name[0]?.toUpperCase()}</div>
            <div className={styles.contactName}>
              {contact.name.formatted_name}
              {contact.org && (
                <div className={styles.contactTitle}>
                  {contact.org.title}
                  {contact.org.title && contact.org.department ? " - " : ""}
                  {contact.org.department}
                  {(contact.org.department || contact.org.title) && contact.org.company ? " - " : ""}
                  {contact.org.company}
                </div>
              )}
            </div>
          </div>
          {contact.phones?.map((phone, index) => (
            <div key={index} className={styles.contactDetail}>
              {phone.phone}
              <span className={styles.contatctType}>{phone.type}</span>
              {startChat && <SvgV2 iconName="chat-now" className={styles.startChat} onClick={() => _startChat(contact, phone.phone)} />}
            </div>
          ))}
          {contact.emails?.map((email, index) => (
            <a href={`mailto:${email.email}`} target="_blank" key={index} className={styles.contactDetail}>
              {email.email}
              <span className={styles.contatctType}>{email.type}</span>
            </a>
          ))}
          {contact.addresses?.map((address, index) => {
            const formatted = [address.country, address.state, address.city, address.street, address.zip].filter(Boolean).join(", ");
            return (
              <a
                href={`https://www.google.com/maps/search/${encodeURIComponent(formatted)}`}
                target="_blank"
                key={index}
                className={styles.contactDetail}
              >
                {formatted}
                <span className={styles.contatctType}>{address.type}</span>
              </a>
            );
          })}
          {contact.birthday && (
            <div className={styles.contactDetail}>
              {contact.birthday}
              <span className={styles.contatctType}>{t("chat_component.contact.birthday")}</span>
            </div>
          )}
          {contact.urls?.map((url, index) => (
            <a href={url.url} target="_blank" key={index} className={styles.contactDetail}>
              {url.url}
              <span className={styles.contatctType}>{url.type}</span>
            </a>
          ))}
        </div>
      ))}
    </BaseModal>
  );
};

export const MessageContacts: React.FC<IContactProps> = ({ contacts, startChat, testId }) => {
  const { t } = useTranslation();

  const onClick = useCallback(() => {
    return messagesModals.openModal(PreviewContactModal, { contacts, startChat });
  }, [contacts, startChat]);
  const onFooterClick = useCallback(
    (e: React.MouseEvent) => {
      if (contacts?.length === 1 && startChat) {
        e.stopPropagation();
        startChat(getContactDetials(contacts[0]));
        return;
      }
    },
    [contacts, startChat]
  );

  if (!contacts?.length) {
    return null;
  }
  return (
    <div className={styles.messageContacts} onClick={onClick}>
      <div className={styles.messageContactsHeader}>
        <div className={styles.messageContactsInitials}>
          {contacts.map((contact, index) => (
            <div key={index} data-testid={`${testId}-icon-${index}`}>
              {contact.name.formatted_name[0]?.toUpperCase()}
            </div>
          ))}
        </div>
        <div className={styles.messageContactsName} data-testid={`${testId}-names`}>
          {t("chat_component.contact.name", {
            count: contacts.length,
            contactName: contacts[0].name.formatted_name,
            numberOfContacts: contacts.length - 1,
          })}
        </div>
      </div>
      <div className={styles.messageContactsFooter} onClick={onFooterClick} data-testid={`${testId}-message-button`}>
        {t("chat_component.contact.footer", { count: contacts.length })}
      </div>
    </div>
  );
};
